/**
 * NEON OVERPASS — segment-based pseudo-3D road.
 *
 * The road is a circular array of segments. Each segment is `SEGMENT_LENGTH`
 * world units of Z, carrying a curve value (how the road bends) and a
 * `world.y` for hills. Rendering projects N segments ahead from the player's
 * camera into screen-space and fills back-to-front polygons.
 *
 * Math is faithful to the reference at `year4.vercel.app/racecar.html`
 * (OutRun-style projection, JavaScript port). The only adaptation we make
 * is replacing `Math.random` with `ctx.random` so track generation is
 * replay-deterministic.
 *
 * Coordinate conventions:
 *   - World Z grows toward the horizon (away from camera)
 *   - World Y is "up"; positive Y = uphill
 *   - World X centers on the road; +X = rightward
 *   - `playerX` is normalized: -1 = left rumble strip, +1 = right rumble strip
 *
 * @see https://codeincomplete.com/articles/javascript-racer/ — the canonical
 *      writeup of this technique
 */

import type { PRNG } from '@arcraz/arcadia-types';
import {
  ROAD_DARK,
  ROAD_FINISH,
  ROAD_LIGHT,
  ROAD_START,
} from './palette.js';

// ============================================================================
// Constants
// ============================================================================

/** Length in world units of a single road segment. */
export const SEGMENT_LENGTH = 200;

/** Half-width of the road in world units. (Full width = 2 * roadWidth.) */
export const ROAD_WIDTH = 2000;

/** Lane count. */
export const LANES = 3;

/** Rumble strip period (a rumble cycles every N segments). */
export const RUMBLE_LENGTH = 3;

/** Camera height above the road. */
export const CAMERA_HEIGHT = 1000;

/** Lens depth — controls field-of-view. Lower = wider FOV. */
export const CAMERA_DEPTH = 0.84;

/** How many segments ahead to render. */
export const DRAW_DISTANCE = 220;

/** Fog density (higher = thicker fog). */
export const FOG_DENSITY = 5;

/** Total laps for an arcade run. */
export const TOTAL_LAPS = 3;

// Track length presets (in segments).
const LEN_SHORT = 25;
const LEN_MEDIUM = 50;
const LEN_LONG = 100;

// Curve magnitude presets.
const CURVE_EASY = 2;
const CURVE_MEDIUM = 4;

// Hill height presets (in world units).
const HILL_LOW = 20;
const HILL_MEDIUM = 40;
const HILL_HIGH = 60;

// ============================================================================
// Types
// ============================================================================

export interface SegmentColors {
  readonly road: string;
  readonly rumble: string;
  readonly grass: string;
  readonly lane: string | null;
}

export interface ProjectedPoint {
  world: { x: number; y: number; z: number };
  camera: { x: number; y: number; z: number };
  screen: { x: number; y: number; w: number; scale: number };
}

export interface RoadsideSprite {
  /** Which sprite kind to draw. Renderer maps id → sprite atlas. */
  readonly kind: 'palm' | 'sign' | 'rock' | 'neonPink' | 'neonCyan';
  /** Lateral offset in road-widths. Negative = left of road, positive = right. */
  readonly offset: number;
  /** World width of the sprite for collision math. */
  readonly worldW: number;
}

export interface OpponentCar {
  /** Lateral offset in road-widths. */
  offset: number;
  /** Absolute Z position around the track. */
  z: number;
  /** Speed in world units per second. */
  speed: number;
  /** Which sprite kind. */
  kind: 'carRed' | 'carBlue' | 'carYellow' | 'carGreen' | 'truck';
  /** World width of the car sprite for collision math. */
  worldW: number;
  /** Fractional position within current segment (for sub-segment lerp). */
  percent: number;
}

export interface Segment {
  /** Index of this segment in the segments array. */
  readonly index: number;
  /** Near-edge projected point (player side). */
  p1: ProjectedPoint;
  /** Far-edge projected point (horizon side). */
  p2: ProjectedPoint;
  /** Curve value applied across this segment. */
  curve: number;
  /** Roadside sprites bound to this segment. */
  sprites: RoadsideSprite[];
  /** Cars currently within this segment (mutates as cars advance). */
  cars: OpponentCar[];
  /** Set true when we render this segment but it has wrapped around. */
  looped: boolean;
  /** Fog factor (1 = no fog, 0 = full fog). Recomputed each render. */
  fog: number;
  /** Screen-Y clip cap, used by sprite rendering. */
  clip: number;
  /** Color band for this segment. */
  color: SegmentColors;
}

// ============================================================================
// Helpers
// ============================================================================

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Exponential fog. d in [0,1]; density controls falloff curve.
 * Returns 1 at the camera, decays toward 0 at the horizon.
 */
export function expFog(d: number, density: number): number {
  return 1 / Math.pow(Math.E, d * d * density);
}

/** Wrap-around addition for circular track length. */
export function increase(start: number, increment: number, max: number): number {
  let v = start + increment;
  while (v >= max) v -= max;
  while (v < 0) v += max;
  return v;
}

function easeIn(a: number, b: number, t: number): number {
  return a + (b - a) * t * t;
}

function easeInOut(a: number, b: number, t: number): number {
  return a + (b - a) * (-Math.cos(t * Math.PI) / 2 + 0.5);
}

/** Find the segment containing world-Z `z`. */
export function findSegment(segments: readonly Segment[], z: number): Segment {
  const idx = Math.floor(z / SEGMENT_LENGTH) % segments.length;
  return segments[idx] as Segment;
}

// ============================================================================
// Track construction
// ============================================================================

class TrackBuilder {
  readonly segments: Segment[] = [];

  private get lastY(): number {
    if (this.segments.length === 0) {
      return 0;
    }
    return this.segments[this.segments.length - 1]!.p2.world.y;
  }

  addSegment(curve: number, y: number): void {
    const n = this.segments.length;
    const isDark = Math.floor(n / RUMBLE_LENGTH) % 2 === 1;
    this.segments.push({
      index: n,
      p1: {
        world: { x: 0, y: this.lastY, z: n * SEGMENT_LENGTH },
        camera: { x: 0, y: 0, z: 0 },
        screen: { x: 0, y: 0, w: 0, scale: 0 },
      },
      p2: {
        world: { x: 0, y, z: (n + 1) * SEGMENT_LENGTH },
        camera: { x: 0, y: 0, z: 0 },
        screen: { x: 0, y: 0, w: 0, scale: 0 },
      },
      curve,
      sprites: [],
      cars: [],
      looped: false,
      fog: 1,
      clip: 0,
      color: isDark ? ROAD_DARK : ROAD_LIGHT,
    });
  }

  addRoad(enter: number, hold: number, leave: number, curve: number, y: number): void {
    const startY = this.lastY;
    const endY = startY + Math.round(y) * SEGMENT_LENGTH;
    const total = enter + hold + leave;
    for (let n = 0; n < enter; n++) {
      this.addSegment(easeIn(0, curve, n / enter), easeInOut(startY, endY, n / total));
    }
    for (let n = 0; n < hold; n++) {
      this.addSegment(curve, easeInOut(startY, endY, (enter + n) / total));
    }
    for (let n = 0; n < leave; n++) {
      this.addSegment(easeInOut(curve, 0, n / leave), easeInOut(startY, endY, (enter + hold + n) / total));
    }
  }

  straight(num = LEN_MEDIUM): void {
    this.addRoad(num, num, num, 0, 0);
  }
  hill(num: number, height: number): void {
    this.addRoad(num, num, num, 0, height);
  }
  curve(num: number, curve: number, height = 0): void {
    this.addRoad(num, num, num, curve, height);
  }
  lowRollingHills(): void {
    const num = LEN_SHORT;
    const h = HILL_LOW;
    this.addRoad(num, num, num, 0, h / 2);
    this.addRoad(num, num, num, 0, -h);
    this.addRoad(num, num, num, CURVE_EASY, h);
    this.addRoad(num, num, num, 0, 0);
    this.addRoad(num, num, num, -CURVE_EASY, h / 2);
    this.addRoad(num, num, num, 0, 0);
  }
  sCurves(): void {
    this.addRoad(LEN_MEDIUM, LEN_MEDIUM, LEN_MEDIUM, -CURVE_EASY, 0);
    this.addRoad(LEN_MEDIUM, LEN_MEDIUM, LEN_MEDIUM, CURVE_MEDIUM, HILL_MEDIUM);
    this.addRoad(LEN_MEDIUM, LEN_MEDIUM, LEN_MEDIUM, CURVE_EASY, -HILL_LOW);
    this.addRoad(LEN_MEDIUM, LEN_MEDIUM, LEN_MEDIUM, -CURVE_EASY, HILL_MEDIUM);
    this.addRoad(LEN_MEDIUM, LEN_MEDIUM, LEN_MEDIUM, -CURVE_MEDIUM, -HILL_MEDIUM);
  }
  downhillToEnd(num = 200): void {
    this.addRoad(num, num, num, -CURVE_EASY, -this.lastY / SEGMENT_LENGTH);
  }
}

/**
 * Build the full track. Determinism: pure function of nothing — same call
 * always produces the same segment geometry (sprite + car placement use
 * the PRNG and are seeded separately).
 */
export function buildTrack(): Segment[] {
  const b = new TrackBuilder();
  b.straight(LEN_SHORT);
  b.lowRollingHills();
  b.straight(LEN_MEDIUM);
  b.sCurves();
  b.curve(LEN_MEDIUM, CURVE_MEDIUM, HILL_LOW);
  b.lowRollingHills();
  b.curve(LEN_LONG, CURVE_MEDIUM, HILL_MEDIUM);
  b.straight();
  b.hill(LEN_MEDIUM, HILL_HIGH);
  b.sCurves();
  b.curve(LEN_LONG, -CURVE_MEDIUM, 0);
  b.curve(LEN_LONG, CURVE_EASY, 0);
  b.hill(LEN_LONG, HILL_HIGH);
  b.curve(LEN_LONG, CURVE_MEDIUM, -HILL_LOW);
  b.lowRollingHills();
  b.curve(LEN_LONG * 2, -CURVE_MEDIUM, HILL_MEDIUM);
  b.straight();
  b.straight(LEN_LONG);
  b.sCurves();
  b.straight(LEN_MEDIUM);
  b.downhillToEnd();

  const segments = b.segments;

  // Start band (near player start).
  if (segments.length > 4) {
    (segments[2] as Segment).color = ROAD_START;
  }
  // Finish bands at the very end.
  for (let n = 0; n < RUMBLE_LENGTH; n++) {
    const i = segments.length - 1 - n;
    if (i >= 0) {
      (segments[i] as Segment).color = ROAD_FINISH;
    }
  }
  return segments;
}

// ============================================================================
// Sprite + opponent placement (deterministic via PRNG)
// ============================================================================

/** Approximate world-width per sprite kind for collision math. */
const SPRITE_WORLD_W: Record<RoadsideSprite['kind'], number> = {
  palm: 700,
  sign: 320,
  rock: 420,
  neonPink: 480,
  neonCyan: 480,
};

const CAR_WORLD_W: Record<OpponentCar['kind'], number> = {
  carRed: 650,
  carBlue: 650,
  carYellow: 650,
  carGreen: 650,
  truck: 850,
};

export function placeSprites(segments: Segment[], rng: PRNG): void {
  for (let n = 18; n < segments.length - 50; n++) {
    const seg = segments[n] as Segment;
    if (rng.next() < 0.18) {
      const side = rng.next() < 0.5 ? -1 : 1;
      const offset = side * (1.4 + rng.next() * 1.6);
      const r = rng.next();
      let kind: RoadsideSprite['kind'];
      if (r < 0.45) kind = 'palm';
      else if (r < 0.65) kind = 'rock';
      else if (r < 0.82) kind = 'neonPink';
      else kind = 'neonCyan';
      seg.sprites.push({ kind, offset, worldW: SPRITE_WORLD_W[kind] });
    }
  }
  // Periodic signs.
  let n = 60;
  while (n < segments.length - 50) {
    const seg = segments[n] as Segment;
    const side = rng.next() < 0.5 ? -1 : 1;
    seg.sprites.push({ kind: 'sign', offset: side * 1.25, worldW: SPRITE_WORLD_W.sign });
    n += 70 + rng.int(0, 50);
  }
}

export function placeCars(
  segments: Segment[],
  rng: PRNG,
  maxSpeed: number
): OpponentCar[] {
  const cars: OpponentCar[] = [];
  // 40 cars — initial bump to 32 still felt sparse in playtesting, so
  // we push density higher and bring up the truck floor (see baseSpeed
  // below) so even slow targets are worth engaging. Per-lap speed
  // multiplier in instance.ts adds further pressure beyond lap 1.
  const n = 40;
  for (let i = 0; i < n; i++) {
    const segIdx = 30 + rng.int(0, segments.length - 80);
    const lane = rng.int(0, 2) - 1; // -1, 0, 1
    const offset = lane * 0.55 + (rng.next() - 0.5) * 0.1;
    const z = segIdx * SEGMENT_LENGTH + rng.next() * SEGMENT_LENGTH;
    const roll = rng.next();
    let kind: OpponentCar['kind'];
    if (roll < 0.22) kind = 'carRed';
    else if (roll < 0.44) kind = 'carBlue';
    else if (roll < 0.62) kind = 'carYellow';
    else if (roll < 0.78) kind = 'carGreen';
    else kind = 'truck';
    // Truck floor bumped from /6 to /5 (sc-2830) — at /6 trucks felt
    // trivially overtakeable. /5 keeps them visibly slower than cars
    // without being free points.
    const baseSpeed = kind === 'truck' ? maxSpeed / 5 : maxSpeed / 4;
    const speed = baseSpeed + rng.next() * (maxSpeed / 4);
    const car: OpponentCar = {
      offset,
      z,
      kind,
      speed,
      worldW: CAR_WORLD_W[kind],
      percent: 0,
    };
    findSegment(segments, z).cars.push(car);
    cars.push(car);
  }
  return cars;
}

// ============================================================================
// Projection
// ============================================================================

/**
 * Project a point from world space to screen space. The standard OutRun
 * pseudo-3D transform: divide by camera-Z for perspective scale, then
 * multiply by half-screen to map normalized coords to pixels.
 */
export function project(
  p: ProjectedPoint,
  camX: number,
  camY: number,
  camZ: number,
  W: number,
  H: number
): void {
  p.camera.x = (p.world.x || 0) - camX;
  p.camera.y = (p.world.y || 0) - camY;
  p.camera.z = (p.world.z || 0) - camZ;
  p.screen.scale = CAMERA_DEPTH / Math.max(0.001, p.camera.z);
  p.screen.x = Math.round(W / 2 + (p.screen.scale * p.camera.x * W) / 2);
  p.screen.y = Math.round(H / 2 - (p.screen.scale * p.camera.y * H) / 2);
  p.screen.w = Math.round((p.screen.scale * ROAD_WIDTH * W) / 2);
}

/** Player Z offset from camera — where the player car sits in world space. */
export const PLAYER_Z = CAMERA_HEIGHT * CAMERA_DEPTH;

// ============================================================================
// Speed constants (derived from maxSpeed for arcade game-feel)
// ============================================================================

/** Top speed in world units per second. */
export const MAX_SPEED = SEGMENT_LENGTH * 60;
export const TURBO_MULT = 1.45;
// Speed-feel pass (sc-2830): faster ramp-up, sharper brake, earlier off-
// road bite. Previously felt like driving a sedan; arcade racers should
// punish indecision.
export const ACCEL = MAX_SPEED / 3;
export const BRAKING = -MAX_SPEED * 1.4;
export const DECEL = -MAX_SPEED / 5;
export const OFF_ROAD_DECEL = -MAX_SPEED / 2;
export const OFF_ROAD_LIMIT = MAX_SPEED / 6;
export const CENTRIFUGAL = 0.32;
